import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import UsersManager from './admin/UsersManager';
import RadiosManager from './admin/RadiosManager';
import GroupsManager from './admin/GroupsManager';
import ActivityLogs from './admin/ActivityLogs';
import LogManager from './admin/LogManager';
import RecordingsManager from './admin/RecordingsManager';
import ConversationsManager from './admin/ConversationsManager';
import SOSSettingsManager from './admin/SOSSettingsManager';
import StatusSettingsManager from './admin/StatusSettingsManager';
import DmrCodeplugProfilesManager from './admin/DmrCodeplugProfilesManager';
import AdminOverview from './admin/AdminOverview';
import AdminSidebar from './admin/AdminSidebar';
import { adminSectionFor, DEFAULT_ADMIN_SECTION, resolveNetworkSelection, type AdminSection } from './admin/adminDashboardModel';
import { can, useIdentity } from '../services/identity';

interface Network {
  id: string;
  name: string;
  tenant_name?: string;
}

const SELECTED_NETWORK_STORAGE_KEY = 'ptt.selectedNetwork';

export default function AdminDashboard() {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const activeSection = adminSectionFor(section);

  const [networks, setNetworks] = useState<Network[]>([]);
  const [networksLoaded, setNetworksLoaded] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { me } = useIdentity();
  const selectedMeta = me?.networks.find((n) => n.id === selectedNetwork);
  const readOnly = !!me && !!selectedNetwork && !can(me, selectedNetwork, 'radios.configure') && !can(me, selectedNetwork, 'groups.manage') && !can(me, selectedNetwork, 'dispatch.operate');
  const departmentScoped = !!selectedMeta && !me?.platform_admin && !selectedMeta.tenant_wide;

  const handleSelectNetwork = (networkId: string) => {
    setSelectedNetwork(networkId);
    localStorage.setItem(SELECTED_NETWORK_STORAGE_KEY, networkId);
  };

  useEffect(() => {
    const fetchNetworks = async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const response = await fetch('/api/admin/organizations');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const list: Network[] = Array.isArray(data) ? data : [];
        setNetworks(list);
        setNetworksLoaded(true);
        // Keep the admin's current selection on a refresh tick while it is still authorized. A
        // network that left the list (access removed, network deleted) is replaced by the one
        // remembered from a previous visit (F5 stays on the same network) if still listed, else
        // by the first; a remembered network that is no longer listed is forgotten.
        const saved = localStorage.getItem(SELECTED_NETWORK_STORAGE_KEY);
        if (saved && !list.some((n) => n.id === saved)) localStorage.removeItem(SELECTED_NETWORK_STORAGE_KEY);
        setSelectedNetwork((prev) => resolveNetworkSelection(prev, list, saved));
      } catch (error) {
        console.error('Failed to fetch networks:', error);
      } finally {
        if (!silent) setLoading(false);
      }
    };

    fetchNetworks();
    const interval = setInterval(() => fetchNetworks(true), 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSectionChange = (newSection: AdminSection) => {
    navigate(`/Admin/${newSection}`);
  };

  if (!activeSection || !section) return <Navigate to={`/Admin/${activeSection ?? DEFAULT_ADMIN_SECTION}`} replace />;

  return (
    <div className="h-full min-h-0 bg-gray-900 text-white">
      <div className="flex h-full min-h-0">
        <AdminSidebar active={activeSection} onSelect={handleSectionChange} />

        {/* Main Content */}
        <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {/* Header */}
          <header className="sticky top-0 z-30 bg-blue-700 shadow-lg shrink-0">
            <div className="px-4 py-4 bg-blue-800 border-b border-blue-700 flex items-center justify-between gap-6">
              <div>
                <h1 className="text-2xl font-bold">Admin Management</h1>
                {(readOnly || departmentScoped) && (
                  <p className="mt-1 text-xs">
                    {readOnly && <span className="mr-2 px-2 py-0.5 rounded bg-yellow-700 text-yellow-100">Read-only access: changes are refused</span>}
                    {departmentScoped && <span className="px-2 py-0.5 rounded bg-purple-700 text-purple-100">Department scope: only your departments' radios and groups</span>}
                  </p>
                )}
                <p className="text-gray-200 text-sm mt-1">System Configuration & Fleet Management</p>
              </div>
              {/* Every section, the Dashboard included, works on the selected network. */}
              <div className="min-w-[220px]">
                <label className="block text-xs font-medium text-blue-200 mb-1">Select Network</label>
                <select
                  value={selectedNetwork}
                  onChange={(e) => handleSelectNetwork(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-900 rounded-lg text-gray-900 bg-white focus:ring-2 focus:ring-blue-300 focus:border-transparent"
                  disabled={loading}
                >
                  {networks.map((network) => (
                    <option key={network.id} value={network.id}>
                      {network.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </header>

          <div className="flex-1 min-h-0 max-w-[1800px] overflow-y-auto px-6 py-8">
            {activeSection === 'Dashboard' && (
              <AdminOverview
                networks={networks}
                networksLoaded={networksLoaded}
                selectedId={selectedNetwork}
                onSelectNetwork={handleSelectNetwork}
                onOpen={handleSectionChange}
              />
            )}
            {activeSection === 'General' && (
              <div className="space-y-6">
                <StatusSettingsManager orgId={selectedNetwork} />
                <SOSSettingsManager orgId={selectedNetwork} />
                <DmrCodeplugProfilesManager orgId={selectedNetwork} />
              </div>
            )}
            {activeSection === 'Radios' && <RadiosManager orgId={selectedNetwork} />}
            {activeSection === 'Groups' && <GroupsManager orgId={selectedNetwork} orgName={networks.find((n) => n.id === selectedNetwork)?.name} />}
            {activeSection === 'Users' && <UsersManager orgId={selectedNetwork} />}
            {activeSection === 'ActivityLogs' && <ActivityLogs orgId={selectedNetwork} />}
            {activeSection === 'Log' && <LogManager orgId={selectedNetwork} />}
            {activeSection === 'Recordings' && <RecordingsManager orgId={selectedNetwork} />}
            {activeSection === 'Conversations' && <ConversationsManager orgId={selectedNetwork} />}
          </div>
        </main>
      </div>
    </div>
  );
}
